import { useDispenseLimitExceededActions } from '@/components/Patient/hooks/useDispenseLimitExceededActions';
import { FF_ENABLE_INITIAL_DR_PAYMENT } from '@/constants/featureFlags';
import { getAuthData } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import type { ConsultPaymentState } from '@/hooks/consult/useConsult';
import { useAppStore } from '@/state-management';
import { Button } from '@montugroup/design-system';
import type { PaymentRequestType } from '@montugroup/pms-api-contracts/build/api-pms/consultation/contracts/request-payment.contract';
import { Box, CircularProgress, Tooltip } from '@mui/material';
import type { CSSProperties } from '@mui/styles';
import { useMemo, useState } from 'react';
import { PrescriptionPadStep } from '../PrescriptionPad/PrescriptionPad.types';

export interface ConsultationConfirmationButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  isLoading?: boolean;
  disabled?: boolean;
  isPatientActive?: boolean;
  style?: CSSProperties;
  paymentState: ConsultPaymentState;
  selectedPaymentRequestType: PaymentRequestType | null;
  shouldNotChargeInitialDoctorConsultation: boolean | null;
}

export const ConsultationConfirmationButton = ({
  onClick,
  disabled,
  isLoading,
  isPatientActive,
  paymentState,
  selectedPaymentRequestType,
  shouldNotChargeInitialDoctorConsultation,
}: ConsultationConfirmationButtonProps) => {
  const { isManager } = getAuthData();
  const {
    prescriptionPad: { step: createScriptStep, actions: reviewAction, medicationPadItems },
    prescriptionHistory: { itemsToAction },
  } = useAppStore.use.prescriptionAssistant();
  const isPrescriptionPadInSync = useAppStore.use.isPrescriptionPadInSync()();
  const isValidPrescriptionPad = useAppStore.use.isValidPrescriptionPad()();
  const hasUserInteractedWithPrescriptions = useAppStore.use.isPrescriptionInProgress()();
  const validPrescriptionMedications = medicationPadItems.filter((item) => !!item.medicationPadItem);

  const [buttonHovered, setButtonHovered] = useState(false);
  const { flags } = useFeatureFlags();
  const ffEnableInitialDrPayment = flags[FF_ENABLE_INITIAL_DR_PAYMENT];
  const { prescriptionPadStep, buttonLabel } = useMemo(() => {
    const prescriptionPadStep = validPrescriptionMedications.length === 0 ? null : createScriptStep;
    let buttonLabel;
    switch (prescriptionPadStep) {
      case PrescriptionPadStep.Create:
        buttonLabel = 'Review Script';
        break;
      case PrescriptionPadStep.Confirm:
        buttonLabel = 'Create Script';
        break;
      case null:
      default:
        buttonLabel = 'Complete Consultation';
        break;
    }

    return {
      prescriptionPadStep,
      buttonLabel,
    };
  }, [validPrescriptionMedications.length, createScriptStep]);

  const disabledDueToUnactionedConfirmCheckList = useMemo(
    () => createScriptStep === PrescriptionPadStep.Confirm && reviewAction.some((action) => !action.checked),
    [createScriptStep, reviewAction],
  );

  const disabledDueToNoPaymentRequestType = useMemo(() => {
    if (shouldNotChargeInitialDoctorConsultation) {
      return false;
    }
    return ffEnableInitialDrPayment && paymentState === 'NOT_PAID' && !selectedPaymentRequestType;
  }, [ffEnableInitialDrPayment, paymentState, selectedPaymentRequestType, shouldNotChargeInitialDoctorConsultation]);

  const tooltipMsg = useMemo(() => {
    if (disabledDueToNoPaymentRequestType) {
      return 'Select the charge type for the consultation.';
    }

    if (!isPrescriptionPadInSync) {
      return 'Prescription data is outdated. Reload to continue.';
    }

    if (disabledDueToUnactionedConfirmCheckList) {
      return 'Please complete the required actions checklist';
    }

    return '';
  }, [isPrescriptionPadInSync, disabledDueToUnactionedConfirmCheckList, disabledDueToNoPaymentRequestType]);

  const { shouldDisable: shouldDisableDueToDispenseLimitExceeded } = useDispenseLimitExceededActions();

  const isDisabled = useMemo(() => {
    if (disabled) {
      return true;
    }

    if (!hasUserInteractedWithPrescriptions && !disabledDueToNoPaymentRequestType) {
      return false;
    }

    const shouldDisable =
      // check if the create script request is loading
      isLoading ||
      // check if the patient is active
      !isPatientActive ||
      // check if the prescription pad is valid
      // check if the prescription pad is in sync
      // check if the prescription pad has items to action
      !isValidPrescriptionPad ||
      !isPrescriptionPadInSync ||
      !!itemsToAction.length ||
      shouldDisableDueToDispenseLimitExceeded ||
      disabledDueToNoPaymentRequestType;

    return shouldDisable || disabledDueToUnactionedConfirmCheckList;
  }, [
    isLoading,
    disabled,
    itemsToAction.length,
    isPatientActive,
    isValidPrescriptionPad,
    isPrescriptionPadInSync,
    disabledDueToUnactionedConfirmCheckList,
    hasUserInteractedWithPrescriptions,
    shouldDisableDueToDispenseLimitExceeded,
    disabledDueToNoPaymentRequestType,
  ]);

  const enabledTooltip =
    ffEnableInitialDrPayment ||
    (disabledDueToUnactionedConfirmCheckList && [PrescriptionPadStep.Confirm, null].includes(prescriptionPadStep)) ||
    !isPrescriptionPadInSync;

  if (isManager) {
    return null;
  }

  return (
    <Tooltip
      open={isDisabled && buttonHovered}
      title={tooltipMsg}
      placement="top-start"
      disableFocusListener
      disableTouchListener
      arrow
    >
      <Box
        onMouseEnter={() => {
          if (enabledTooltip) {
            setButtonHovered(true);
          }
        }}
        onMouseLeave={() => setButtonHovered(false)}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={onClick}
          disabled={isDisabled}
          sx={{ borderRadius: 1, textTransform: 'none', minHeight: '38px' }}
          startIcon={isLoading ? <CircularProgress size={16} /> : null}
        >
          {buttonLabel}
        </Button>
      </Box>
    </Tooltip>
  );
};
